/**
 * LogCommunicationModal — record a phone call, meeting, letter, or other contact.
 *
 * Differs from upload-document-modal in that the *event* is the record; file
 * attachments are optional. The form is responsive enough to be usable on a
 * phone so a manager can log a call between meetings.
 *
 * "Internal note" is a special type that locks Direction = Internal and
 * hides the counterparty field.
 *
 * No threading in this patch — the "In reply to" field is added in Patch 2.
 */
import { useEffect, useMemo, useState } from 'react';
import { Phone, Calendar, Mail, MessageSquare, Send, ArrowDown, FileText, Upload, X as XIcon, NotebookPen } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/form-field';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { Spinner } from '@/components/ui/spinner';
import { TagInput } from './tag-input';
import { AnchorPicker, type EntityOption } from './anchor-picker';
import { useAnchorEntities } from '@/lib/use-anchor-entities';
import { describeUploadError, useSystemHealth } from '@/lib/system-health';
import { useDepartmentsListAll } from '@/lib/masters-queries';
import {
  uploadFileToMinio,
  useAttachment,
  useLogCommunication,
  type AttachmentAnchor,
  type CommDirection,
  type CommType,
  type CommunicationAttachedDoc,
  type DocumentType,
} from '@/lib/attachments-queries';

const COMM_TYPES: { value: CommType; label: string; icon: typeof Phone }[] = [
  { value: 'PHONE_CALL', label: 'Phone call', icon: Phone },
  { value: 'MEETING', label: 'Meeting', icon: Calendar },
  { value: 'LETTER_RECEIVED', label: 'Letter received', icon: ArrowDown },
  { value: 'LETTER_SENT', label: 'Letter sent', icon: Send },
  { value: 'EMAIL', label: 'Email', icon: Mail },
  { value: 'WHATSAPP', label: 'WhatsApp', icon: MessageSquare },
  { value: 'SMS', label: 'SMS', icon: MessageSquare },
  { value: 'INTERNAL_NOTE', label: 'Internal note', icon: NotebookPen },
];

interface AnchorEntities {
  parts: EntityOption[];
  batches: EntityOption[];
  bankGuarantees: EntityOption[];
  inspectionEvents: EntityOption[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * When set, scopes the anchor picker to this project. When null, the picker
   * shows full project / master selection — used from the cross-project page
   * and from reply flows where the parent anchor is master-only.
   */
  projectId: string | null;
  defaultAnchor?: AttachmentAnchor;
  /** Pre-fill direction (e.g., a "Log follow-up" CTA hints outbound). */
  defaultDirection?: CommDirection;
  /** Entities to populate the per-project sub-anchor selects. Required iff projectId. */
  entities?: AnchorEntities;
  /** Patch 2 — projects to choose from when projectId is null. */
  projects?: EntityOption[];
  /** Patch 2 — master entities for the master-anchor picker. */
  clients?: EntityOption[];
  suppliers?: EntityOption[];
  departments?: EntityOption[];
  /**
   * Patch 2 — reply mode. When set, the new message joins the parent's thread,
   * inheriting the parent's anchor, counterparty, subject (no "Re:"), and tags,
   * with direction flipped to the opposite. User can override any field.
   */
  replyToCommId?: string | null;
}

const MAX_FILE_SIZE = 250 * 1024 * 1024;

function nowIsoMinute(): string {
  // Local "YYYY-MM-DDTHH:MM" for the datetime-local input.
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const EMPTY_ANCHOR: AttachmentAnchor = {
  projectId: null,
  projectItemId: null,
  orderBatchId: null,
  bankGuaranteeId: null,
  inspectionEventId: null,
  clientId: null,
  supplierId: null,
  masterDepartmentId: null,
};

function flipDirection(d: CommDirection): CommDirection {
  if (d === 'INBOUND') return 'OUTBOUND';
  if (d === 'OUTBOUND') return 'INBOUND';
  return 'INTERNAL';
}

export function LogCommunicationModal({
  open,
  onClose,
  projectId,
  defaultAnchor,
  defaultDirection,
  entities,
  projects = [],
  clients = [],
  suppliers = [],
  departments = [],
  replyToCommId,
}: Props) {
  const [commType, setCommType] = useState<CommType>('PHONE_CALL');
  const [direction, setDirection] = useState<CommDirection>(defaultDirection ?? 'OUTBOUND');
  const [occurredAt, setOccurredAt] = useState(nowIsoMinute());
  const [counterpartyName, setCounterpartyName] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [communicationDepartmentId, setCommunicationDepartmentId] = useState<string | null>(null);
  const [copiedDepartmentIds, setCopiedDepartmentIds] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [anchor, setAnchor] = useState<AttachmentAnchor>(
    defaultAnchor ?? { ...EMPTY_ANCHOR, projectId: projectId ?? null },
  );
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [replyPreFillApplied, setReplyPreFillApplied] = useState(false);
  const logComm = useLogCommunication();
  const parentQ = useAttachment(replyToCommId ?? undefined);
  // Patch 3 — context for error copy when attached-file upload fails
  const health = useSystemHealth();
  const storageDown = health.data?.storage.ok === false;

  // Bug 6/8 fix — load sub-anchor entities (parts/batches/BGs/inspections)
  // based on whichever project is currently selected in the anchor. This
  // reacts to the user picking a project in the modal's project dropdown
  // (Communications page), and supersedes the legacy `entities` prop that
  // callers used to pass in (and which previously hardcoded `batches: []`
  // and `inspectionEvents: []`). The hook is gated by `enabled` so it
  // doesn't fire requests when no project is in scope.
  const liveEntities = useAnchorEntities(anchor.projectId ?? projectId ?? null);
  const fallbackDepartmentsQ = useDepartmentsListAll();
  const effectiveDepartments = useMemo(() => {
    if (departments.length > 0) return departments;
    return (fallbackDepartmentsQ.data ?? []).map((d) => ({ id: d.id, label: d.name }));
  }, [departments, fallbackDepartmentsQ.data]);
  const departmentSelectItems = useMemo(
    () => effectiveDepartments.map((d) => ({ value: d.id, label: d.label })),
    [effectiveDepartments],
  );

  // Patch 2 — pre-fill from parent when in reply mode. Only runs once per open
  // to avoid clobbering user edits.
  useEffect(() => {
    if (!open || !replyToCommId) return;
    if (replyPreFillApplied) return;
    const p = parentQ.data;
    if (!p) return;
    setSubject(p.subject ?? '');
    setCounterpartyName(p.counterpartyName ?? '');
    setReferenceNumber(p.referenceNumber ?? '');
    setCommunicationDepartmentId(p.communicationDepartmentId ?? null);
    setCopiedDepartmentIds((p.copiedDepartments ?? []).map((d) => d.id));
    setTags(p.tags ?? []);
    setDirection(p.direction ? flipDirection(p.direction) : 'OUTBOUND');
    setAnchor({
      projectId: p.projectId,
      projectItemId: p.projectItemId,
      orderBatchId: p.orderBatchId,
      bankGuaranteeId: p.bankGuaranteeId,
      inspectionEventId: p.inspectionEventId,
      clientId: p.clientId,
      supplierId: p.supplierId,
      masterDepartmentId: p.masterDepartmentId,
    });
    setReplyPreFillApplied(true);
  }, [open, replyToCommId, parentQ.data, replyPreFillApplied]);

  const isInternalNote = commType === 'INTERNAL_NOTE';
  const effectiveDirection: CommDirection = isInternalNote ? 'INTERNAL' : direction;

  const reset = () => {
    setCommType('PHONE_CALL');
    setDirection(defaultDirection ?? 'OUTBOUND');
    setOccurredAt(nowIsoMinute());
    setCounterpartyName('');
    setReferenceNumber('');
    setCommunicationDepartmentId(null);
    setCopiedDepartmentIds([]);
    setSubject('');
    setNotes('');
    setTags([]);
    setAnchor(
      defaultAnchor ?? { ...EMPTY_ANCHOR, projectId: projectId ?? null },
    );
    setAttachedFiles([]);
    setErrorMsg(null);
    setSubmitting(false);
    setReplyPreFillApplied(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const onAddFiles = (files: FileList | null) => {
    if (!files) return;
    setErrorMsg(null);
    const next: File[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_FILE_SIZE) {
        setErrorMsg(`"${f.name}" is too large (${humanSize(f.size)}). Maximum is 250 MB per file.`);
        continue;
      }
      next.push(f);
    }
    setAttachedFiles((prev) => [...prev, ...next]);
  };

  const removeFile = (idx: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const canSubmit =
    subject.trim().length > 0 && occurredAt.length > 0 && !!commType && !submitting;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setErrorMsg(null);
    setSubmitting(true);
    try {
      // Upload any attached files first
      const attachedDocs: CommunicationAttachedDoc[] = [];
      for (const f of attachedFiles) {
        const { storageKey } = await uploadFileToMinio(f);
        attachedDocs.push({
          storageKey,
          fileSizeBytes: f.size,
          fileMimeType: f.type || 'application/octet-stream',
          displayName: f.name.replace(/\.[^.]+$/, ''),
          documentType: inferDocumentType(commType),
          documentDate: occurredAt.slice(0, 10),
        });
      }
      await logComm.mutateAsync({
        commType,
        direction: effectiveDirection,
        occurredAt: new Date(occurredAt).toISOString(),
        counterpartyName:
          effectiveDirection === 'INTERNAL' ? null : counterpartyName.trim() || null,
        referenceNumber: referenceNumber.trim() || null,
        communicationDepartmentId,
        copiedDepartmentIds,
        subject: subject.trim(),
        notes: notes.trim() || null,
        anchor,
        tags,
        attachedDocs: attachedDocs.length > 0 ? attachedDocs : undefined,
        inReplyToId: replyToCommId ?? null,
      });
      reset();
      onClose();
    } catch (err) {
      setErrorMsg(describeUploadError(err, storageDown));
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Log communication"
      size="md"
      lockBackdrop={submitting}
      footer={
        <>
          <Button variant="tertiary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit}>
            {submitting ? (
              <span className="inline-flex items-center gap-1">
                <Spinner className="h-3 w-3" />
                Logging…
              </span>
            ) : (
              'Log'
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Type">
          <div className="flex flex-wrap gap-1.5">
            {COMM_TYPES.map((t) => {
              const Icon = t.icon;
              const checked = commType === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setCommType(t.value)}
                  className={
                    // Patch 3 — mobile gets ≥44px tap target; desktop stays dense.
                    'inline-flex min-h-[40px] items-center gap-1.5 rounded-sm border px-3 py-2 text-sm transition-colors sm:min-h-0 sm:gap-1 sm:px-2 sm:py-1 sm:text-xs ' +
                    (checked
                      ? 'border-accent bg-accent/5 text-accent-strong'
                      : 'border-border-default text-secondary hover:border-border-strong hover:text-primary')
                  }
                >
                  <Icon className="h-4 w-4 sm:h-3 sm:w-3" strokeWidth={1.5} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </Field>

        {!isInternalNote ? (
          <Field label="Direction">
            <div className="flex gap-2 sm:gap-1.5">
              {(['INBOUND', 'OUTBOUND'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDirection(d)}
                  className={
                    'inline-flex min-h-[40px] flex-1 items-center justify-center rounded-sm border px-4 py-2 text-sm transition-colors sm:min-h-0 sm:flex-none sm:px-2.5 sm:py-1 sm:text-xs ' +
                    (direction === d
                      ? 'border-accent bg-accent/5 text-accent-strong'
                      : 'border-border-default text-secondary hover:border-border-strong hover:text-primary')
                  }
                >
                  {d === 'INBOUND' ? 'Inbound' : 'Outbound'}
                </button>
              ))}
            </div>
          </Field>
        ) : (
          <div className="text-xs text-secondary">
            Internal notes have no direction or counterparty.
          </div>
        )}

        <Field label="When">
          <Input
            type="datetime-local"
            value={occurredAt}
            onChange={(e) => setOccurredAt(e.target.value)}
          />
        </Field>

        {!isInternalNote ? (
          <Field label="Counterparty (optional)">
            <Input
              value={counterpartyName}
              onChange={(e) => setCounterpartyName(e.target.value)}
              placeholder="e.g. Wg Cmdr Khan (PAF)"
            />
          </Field>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Reference number (optional)">
            <Input
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder="Letter / email / file reference"
            />
          </Field>
          <Field label="Department (optional)">
            <SearchableSelect
              items={departmentSelectItems}
              value={communicationDepartmentId}
              onChange={setCommunicationDepartmentId}
              placeholder="No department"
              emptyMessage="No departments found"
              clearable
            />
          </Field>
        </div>

        <Field label="Copied to departments (optional)">
          <SearchableMultiSelect
            items={departmentSelectItems}
            values={copiedDepartmentIds}
            onChange={(values) =>
              setCopiedDepartmentIds(values ?? departmentSelectItems.map((item) => item.value))
            }
            placeholder="All departments"
            noneLabel="No copied departments"
            emptyMessage="No departments found"
          />
        </Field>

        <Field label="Subject" required>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Short summary"
            required
          />
        </Field>

        <Field label="Notes (optional)">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="What was discussed, what was decided, follow-ups…"
          />
        </Field>

        <AnchorPicker
          value={anchor}
          onChange={setAnchor}
          projectId={projectId}
          projects={projects}
          // Live-loaded entities take precedence; if a caller still passes
          // `entities` (legacy), we union the two so callers that supply
          // their own pre-loaded lists still work. The hook returns empty
          // arrays when no projectId is in scope.
          parts={liveEntities.parts.length > 0 ? liveEntities.parts : (entities?.parts ?? [])}
          batches={liveEntities.batches.length > 0 ? liveEntities.batches : (entities?.batches ?? [])}
          bankGuarantees={
            liveEntities.bankGuarantees.length > 0
              ? liveEntities.bankGuarantees
              : (entities?.bankGuarantees ?? [])
          }
          inspectionEvents={
            liveEntities.inspectionEvents.length > 0
              ? liveEntities.inspectionEvents
              : (entities?.inspectionEvents ?? [])
          }
          clients={clients}
          suppliers={suppliers}
          departments={effectiveDepartments}
          // Bug 3 fix — the prior rule was `projectId ? ['PROJECT'] :
          // ['PROJECT', 'MASTER']`, which hard-suppressed master mode from
          // every project surface, including the project-header CTA where it
          // *should* be available (a supplier letter that arrived during the
          // project is master-anchored). Now we infer from data presence:
          // if the caller passed master lists, allow master mode; otherwise
          // keep the old project-only behaviour. The Documents tab inside a
          // project doesn't pass master lists, so it stays PROJECT-only as
          // before — no change there.
          allowedKinds={
            projectId
              ? (clients?.length || suppliers?.length || effectiveDepartments.length
                  ? ['PROJECT', 'MASTER']
                  : ['PROJECT'])
              : ['PROJECT', 'MASTER']
          }
        />

        <Field label="Tags">
          <TagInput value={tags} onChange={setTags} />
        </Field>

        <Field label="Attach files (optional)">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => {
                const inp = document.createElement('input');
                inp.type = 'file';
                inp.multiple = true;
                inp.onchange = () => onAddFiles(inp.files);
                inp.click();
              }}
              className="inline-flex items-center gap-1 rounded-sm border border-dashed border-border-default px-3 py-1.5 text-xs text-secondary hover:border-border-strong hover:text-primary"
            >
              <Upload className="h-3 w-3" strokeWidth={1.5} />
              Choose files
            </button>
            {attachedFiles.length > 0 ? (
              <ul className="space-y-1">
                {attachedFiles.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-sm border border-border-default bg-base/40 px-2 py-1 text-xs"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-3 w-3 shrink-0 text-tertiary" strokeWidth={1.5} />
                      <span className="truncate text-primary">{f.name}</span>
                      <span className="shrink-0 text-secondary">{humanSize(f.size)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      disabled={submitting}
                      aria-label={`Remove ${f.name}`}
                      className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-status-danger disabled:opacity-50"
                    >
                      <XIcon className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </Field>

        {errorMsg ? <p className="text-xs text-status-danger">{errorMsg}</p> : null}
      </div>
    </Modal>
  );
}

/**
 * Heuristic: pick a default DocumentType for files attached to a communication.
 * Letters scan in as LETTER, emails as EMAIL_SCAN, anything else as OTHER. The
 * user can refine the type from the document detail drawer.
 */
function inferDocumentType(comm: CommType): DocumentType {
  if (comm === 'LETTER_RECEIVED' || comm === 'LETTER_SENT') return 'LETTER';
  if (comm === 'EMAIL') return 'EMAIL_SCAN';
  return 'OTHER';
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-tertiary">
        {label}
        {required ? <span className="ml-0.5 text-danger-strong">*</span> : null}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
